import { mulberry32 } from "./rng";

// Operator names v0: the squad members of an Operation are people, not
// pips. The roster is drawn deterministically from the run seed; survivors
// carry by name between missions, losses stay lost, resupply brings a fresh
// face. (Ranks, XP, wounds, and traits are the full roster system —
// docs/design/GDD.md §The Squad Roster; this is its foundation.)

export const OPERATOR_NAMES: string[] = [
  "VOSS", "REYES", "OKAFOR", "MERCER", "DIETRICH", "KOWALSKI",
  "TRAN", "BEAUMONT", "ASHFORD", "GUZMAN", "HALE", "IVERSEN",
  "LARKIN", "MARSH", "NOVAK", "ORTIZ", "PRICE", "QUINN",
  "RHODES", "SATO", "THORNE", "VANCE", "WEBER", "YATES",
];

// The run's full draft order of names — a seeded shuffle. The first N are
// the starting squad; later entries arrive via resupply / attachment.
export function operatorDraftOrder(seed: number): string[] {
  const rng = mulberry32((seed ^ 0x0be5701) | 0);
  const pool = [...OPERATOR_NAMES];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [pool[i], pool[j]] = [pool[j]!, pool[i]!];
  }
  return pool;
}

export function generateRoster(seed: number, count: number): string[] {
  return operatorDraftOrder(seed).slice(0, count);
}

// Next unused name in the seeded draft order (deterministic replacement).
export function nextReserveName(seed: number, inUseOrLost: readonly string[]): string {
  const used = new Set(inUseOrLost);
  for (const name of operatorDraftOrder(seed)) {
    if (!used.has(name)) return name;
  }
  // Pool exhausted (24 names — effectively unreachable in a 5-node run)
  return `REPL-${(inUseOrLost.length + 1).toString().padStart(2, "0")}`;
}

// ── Persistent operator roster (docs/design/GDD.md §The Squad Roster) ──────
// Eight named operators live on the PROFILE. Runs deploy the ready ones;
// deaths mid-run wound them, a squad wipe kills them for good. Ranks come
// from XP; traits are derived deterministically from name+rank so they need
// no storage or migration.

export const PROFILE_ROSTER_SIZE = 8;
export const VETERAN_XP = 3;
export const ELITE_XP = 8;

export type OperatorStatus = "ready" | "wounded";

export type OperatorRecord = {
  name: string;
  xp: number;
  status: OperatorStatus;
  // Runs left to sit out (only meaningful while wounded)
  woundedRuns: number;
};

export type OperatorRank = "RECRUIT" | "VETERAN" | "ELITE";

export function rankFor(xp: number): OperatorRank {
  if (xp >= ELITE_XP) return "ELITE";
  if (xp >= VETERAN_XP) return "VETERAN";
  return "RECRUIT";
}

export type OperatorTrait = {
  id: "medic" | "iron-will" | "suppressor" | "marksman";
  name: string;
  desc: string;
};

export const TRAITS: OperatorTrait[] = [
  { id: "medic", name: "COMBAT MEDIC", desc: "Prevents one squad death per mission" },
  { id: "iron-will", name: "IRON WILL", desc: "Squad takes 15% less damage" },
  { id: "suppressor", name: "SUPPRESSOR", desc: "Enemy accuracy reduced 15%" },
  { id: "marksman", name: "MARKSMAN", desc: "Squad return fire 50% more effective" },
];

function nameHash(name: string): number {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (Math.imul(h, 31) + name.charCodeAt(i)) | 0;
  return h >>> 0;
}

// Veteran unlocks the first trait, Elite a second (always distinct).
export function traitsFor(name: string, xp: number): OperatorTrait[] {
  const rank = rankFor(xp);
  if (rank === "RECRUIT") return [];
  const h = nameHash(name);
  const first = TRAITS[h % TRAITS.length]!;
  if (rank === "VETERAN") return [first];
  const second = TRAITS[(h + 1 + (h % (TRAITS.length - 1))) % TRAITS.length]!;
  return second.id === first.id ? [first, TRAITS[(h + 1) % TRAITS.length]!] : [first, second];
}

// The 1989 baseline roster — same eight for every new profile.
export function defaultProfileRoster(): OperatorRecord[] {
  return operatorDraftOrder(0x1989)
    .slice(0, PROFILE_ROSTER_SIZE)
    .map((name) => ({ name, xp: 0, status: "ready" as const, woundedRuns: 0 }));
}

// A fresh recruit to replace a KIA — first pool name not already used.
export function freshRecruitName(existing: readonly string[]): string {
  const used = new Set(existing);
  for (const name of OPERATOR_NAMES) {
    if (!used.has(name)) return name;
  }
  return `REPL-${(existing.length + 1).toString().padStart(2, "0")}`;
}

// What the deployed team's traits do to the mission, aggregated.
export type RosterModifiers = {
  squadDamageTakenMult: number;
  enemyHitChanceMult: number;
  squadFireEffectMult: number;
  deathPreventions: number;
};

export function computeRosterModifiers(deployed: readonly OperatorRecord[]): RosterModifiers {
  const out: RosterModifiers = {
    squadDamageTakenMult: 1,
    enemyHitChanceMult: 1,
    squadFireEffectMult: 1,
    deathPreventions: 0,
  };
  for (const op of deployed) {
    for (const trait of traitsFor(op.name, op.xp)) {
      if (trait.id === "iron-will") out.squadDamageTakenMult *= 0.85;
      else if (trait.id === "suppressor") out.enemyHitChanceMult *= 0.85;
      else if (trait.id === "marksman") out.squadFireEffectMult *= 1.5;
      else if (trait.id === "medic") out.deathPreventions += 1;
    }
  }
  return out;
}

// ── Operator vignettes (docs/design/HUB.md §Room 4) ─────────────────────────
// One paragraph of character, unlocked at rank. Not gameplay — the reason a
// KIA hurts. Deterministic from the name hash, {NAME} woven in.

const VETERAN_VIGNETTES: readonly string[] = [
  "{NAME} doesn't sleep before missions. Cleans a weapon instead. Asked once what they think about while doing it: 'Nothing. That's the point.'",
  "{NAME} keeps a paperback in the left chest pocket. Same one since selection. Won't say what it is and nobody's managed a look at the spine.",
  "{NAME} writes a letter before every deployment and never mails any of them. The stack is rubber-banded in a boot box marked LATER.",
  "{NAME} can name the aircraft by engine sound before the radio call comes in. Claims everybody can. Everybody cannot.",
  "{NAME} carries hot sauce in a mag pouch and shares it exactly once with each new operator. It's a test. Nobody knows the passing criteria.",
  "{NAME} laughs at precisely one joke, told by a teammate who didn't come home from Grenada. Still tells it to new people, deadpan, waiting.",
  "{NAME} does the crossword from papers three weeks old and complains the news is wrong, not late.",
  "{NAME} learned the medic's job unofficially, after a bad night nobody discusses. The aid bag rides closer to hand than the rifle now.",
];

const ELITE_VIGNETTES: readonly string[] = [
  "There is a photo of {NAME}'s squad from '86. {NAME} keeps it face-down in a footlocker and looks at it exactly once, the night before every op. Six faces. Two still answer letters.",
  "The younger operators call {NAME} 'sir' by accident and get corrected every time: 'I work for a living.' They keep doing it. {NAME} keeps correcting. It's a ritual now, and rituals keep people alive.",
  "{NAME} was offered an instructor billet stateside. Turned it down twice. The third time the paperwork arrived pre-approved, {NAME} lost it. It has not been found. It will not be found.",
  "When the new ones ask {NAME} how you get used to it, the answer is always the same: 'You don't. You get useful instead.' Then chow, like nothing was said.",
  "{NAME} has walked out of two aircraft that didn't fly again. Doesn't talk about either. But on every takeoff, one gloved hand rests flat on the airframe — just for a second. Just to say: not this one.",
  "Command tried to build a file on what makes {NAME} effective. The assessment came back one line long: 'Refuses to be surprised.'",
];

// The vignette that matches the operator's current rank, or null below
// VETERAN. Elites get the deeper cut.
export function vignetteFor(name: string, xp: number): string | null {
  const rank = rankFor(xp);
  if (rank === "RECRUIT") return null;
  const h = nameHash(name);
  const pool = rank === "ELITE" ? ELITE_VIGNETTES : VETERAN_VIGNETTES;
  return pool[h % pool.length]!.replaceAll("{NAME}", name);
}
